/*
 * 照片假解冻(冷归档取回)团队级状态。
 *
 * 放 store 而不是组件局部：解冻横条、进度弹窗、照片蒙层共享同一份任务状态，
 * 且后续要覆盖全部照片页、照片台账表、分类文件夹详情页三个页面。
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  activateTemporaryThawTask,
  fetchTemporaryThawDetail,
  fetchTemporaryThawSummary
} from '../pages/components/PhotoThaw/photoThawApi'
import type {
  TemporaryThawDetail,
  TemporaryThawSummary
} from '../pages/components/PhotoThaw/photoThawApi'
import type { RootState } from '../app/store'

interface PhotoThawState {
  /** 摘要，驱动照片蒙层与悬浮横条；null 表示未请求或接口不可用 */
  summary: TemporaryThawSummary | null
  /** 任务详情，驱动进度弹窗；接口失败时保留上一次的值，不清空 */
  detail: TemporaryThawDetail | null
  /** 激活请求进行中，用于防止重复点击 */
  activating: boolean
  /** 进度弹窗是否打开 */
  progressModalOpen: boolean
  /**
   * 当前视窗里有没有冷冻照片。由照片列表在滚动时写入，悬浮横条据此决定是否露出。
   * 查询结果同时包含正常照片和冷冻照片时，停在正常照片区域不弹横条。
   */
  restrictedPhotoInView: boolean
  /**
   * 本次会话内解冻是不是由本端触发的。只给埋点用：横条的 popTitle 要区分
   * 「开始解冻首次提示条」与「解冻中常驻提示条」，两者在 UI 上是同一个横条，
   * 只能靠「这次解冻是不是刚在本页面点出来的」来分。切换团队时随 reset 清掉。
   */
  activatedInSession: boolean
}

const initialState: PhotoThawState = {
  summary: null,
  detail: null,
  activating: false,
  progressModalOpen: false,
  restrictedPhotoInView: false,
  activatedInSession: false
}

export const fetchThawSummary = createAsyncThunk<
  TemporaryThawSummary | null,
  string
>('photoThaw/fetchSummary', (groupID) => fetchTemporaryThawSummary(groupID))

export const fetchThawDetail = createAsyncThunk<
  TemporaryThawDetail | null,
  string
>('photoThaw/fetchDetail', (groupID) => fetchTemporaryThawDetail(groupID))

/**
 * 激活解冻任务。成功后重新拉取摘要，让横条与照片蒙层切到「解冻中」。
 * 照片列表本身的 temporaryPhotoThaw 字段要由调用方重新请求照片才会更新。
 */
export const activateThawTask = createAsyncThunk<boolean, string>(
  'photoThaw/activate',
  async (groupID, { dispatch }) => {
    const activated = await activateTemporaryThawTask(groupID)
    if (activated) {
      await dispatch(fetchThawSummary(groupID))
    }
    return activated
  }
)

const photoThawSlice = createSlice({
  name: 'photoThaw',
  initialState,
  reducers: {
    setProgressModalOpen: (state, action: PayloadAction<boolean>) => {
      state.progressModalOpen = action.payload
    },
    setRestrictedPhotoInView: (state, action: PayloadAction<boolean>) => {
      state.restrictedPhotoInView = action.payload
    },
    /** 切换团队时清空，避免上个团队的照片数短暂显示在新团队上 */
    reset: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThawSummary.fulfilled, (state, action) => {
        state.summary = action.payload
      })
      .addCase(fetchThawDetail.fulfilled, (state, action) => {
        // 接口失败返回 null 时保留上一次的详情，弹窗不会突然空掉
        if (action.payload) state.detail = action.payload
      })
      .addCase(activateThawTask.pending, (state) => {
        state.activating = true
      })
      .addCase(activateThawTask.fulfilled, (state, action) => {
        state.activating = false
        if (action.payload) state.activatedInSession = true
      })
      .addCase(activateThawTask.rejected, (state) => {
        state.activating = false
      })
  }
})

export const { setProgressModalOpen, setRestrictedPhotoInView, reset } =
  photoThawSlice.actions

export const selectThawSummary = (state: RootState) =>
  state.photoThaw?.summary ?? null
export const selectThawDetail = (state: RootState) =>
  state.photoThaw?.detail ?? null
export const selectThawActivating = (state: RootState) =>
  state.photoThaw?.activating ?? false
export const selectThawProgressModalOpen = (state: RootState) =>
  state.photoThaw?.progressModalOpen ?? false
export const selectRestrictedPhotoInView = (state: RootState) =>
  state.photoThaw?.restrictedPhotoInView ?? false
export const selectThawActivatedInSession = (state: RootState) =>
  state.photoThaw?.activatedInSession ?? false

export default photoThawSlice.reducer
